import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
} from "react";

// Create context with meaningful defaults to avoid undefined errors
const defaultContextValue = {
  sections: [],
  addSection: () => console.warn("EmailSectionProvider not found"),
  updateSection: () => console.warn("EmailSectionProvider not found"),
  removeSection: () => console.warn("EmailSectionProvider not found"),
  setAllSections: () => console.warn("EmailSectionProvider not found"),
  getSectionsObject: () => ({}),
};

const EmailSectionContext = createContext(defaultContextValue);

export const EmailSectionProvider = ({ children, initialSections = {} }) => {
  // Improve normalizeSections to better handle data structures
  const normalizeSections = useCallback((sections) => {
    if (Array.isArray(sections)) {
      if (sections.length === 0) {
        return [];
      }

      const normalized = sections
        .filter((section) => {
          // Filter out null/undefined items and ensure they have component_name
          if (!section) {
            console.warn("Filtering out null/undefined section");
            return false;
          }
          if (!section.component_name) {
            console.warn(
              "Filtering out section without component_name:",
              section
            );
            return false;
          }
          return true;
        })
        .map((section) => {
          // Ensure each section has an ID
          if (section.id) return section;
          const newId = `section-${Date.now()}-${Math.random()
            .toString(36)
            .substr(2, 9)}`;
          return { id: newId, ...section };
        });
      return normalized;
    } else if (typeof sections === "object" && sections !== null) {
      // If there are no keys or no valid sections, return empty array
      const keys = Object.keys(sections);
      if (keys.length === 0) {
        console.warn("Received empty object as sections data");
        return [];
      }

      // Check if it's already using the new format (containing 'sections' array)
      if (sections.sections && Array.isArray(sections.sections)) {
        // Only use the sections array if it's not empty
        if (sections.sections.length > 0) {
          const result = normalizeSections(sections.sections);
          return result;
        } else {
          // If sections array is empty but we have other valid section objects, use those instead
          const otherKeys = keys.filter((key) => key !== "sections");

          if (otherKeys.length === 0) {
            return [];
          }

          // Continue processing with the rest of the object
        }
      }

      const normalized = Object.entries(sections)
        .filter(([key, data]) => {
          // Skip the 'sections' key if it's an empty array
          if (key === "sections" && Array.isArray(data) && data.length === 0) {
            return false;
          }

          // Filter out entries without valid component data
          if (!data || typeof data !== "object") {
            console.warn(`Filtering out invalid section at key ${key}:`, data);
            return false;
          }
          if (!data.component_name) {
            console.warn(
              `Filtering out section without component_name at key ${key}:`,
              data
            );
            return false;
          }
          return true;
        })
        .map(([key, data]) => {
          // Handle case where data might already have an id
          const id = data.id || key;
          // Ensure we don't duplicate the id property
          const { id: _, ...restData } = data.id ? data : data;
          return { id, ...restData };
        });
      return normalized;
    }
    return [];
  }, []);

  const [sections, setSections] = useState(() => {
    const normalized = normalizeSections(initialSections);
    return normalized;
  });

  useEffect(() => {
    const normalized = normalizeSections(initialSections);
    if (normalized.length > 0) {
      setSections(normalized);
    }
  }, [initialSections, normalizeSections]);

  // Add a new section (at the end)
  const addSection = useCallback(
    (sectionId, sectionData) => {
      // Check if this section already exists to prevent duplicates
      const existingSection = sections.find((s) => s.id === sectionId);
      if (existingSection) {
        return;
      }

      setSections((prev) => {
        const newSections = [...prev, { id: sectionId, ...sectionData }];
        return newSections;
      });
    },
    [sections]
  );

  // Improve update section to handle different data structures
  const updateSection = useCallback(
    (sectionId, updatedData) => {
      setSections((prev) => {
        // Find the section to update
        const sectionIndex = prev.findIndex(
          (section) => section.id === sectionId
        );

        if (sectionIndex === -1) {
          return prev;
        }

        // Create a new array with the updated section
        const newSections = [...prev];
        newSections[sectionIndex] = {
          ...prev[sectionIndex],
          ...updatedData,
          // Always ensure id is preserved
          id: sectionId,
        };

        return newSections;
      });
    },
    [sections]
  );

  // Remove a section by id
  const removeSection = useCallback(
    (sectionId) => {
      setSections((prev) => {
        const newSections = prev.filter((section) => section.id !== sectionId);
        return newSections;
      });
    },
    [sections]
  );

  // Set all sections (for drag-and-drop or reset)
  const setAllSections = useCallback(
    (newSections) => {
      // Accepts array of {id, ...data} or object

      const normalized = normalizeSections(newSections);
      setSections(normalized);
    },
    [normalizeSections, sections]
  );

  // Helper: get sections as object (for legacy compatibility)
  const getSectionsObject = useCallback(() => {
    const obj = {};
    sections.forEach((section) => {
      if (!section || !section.id) {
        console.warn("Found invalid section without ID:", section);
        return;
      }
      const { id, ...sectionData } = section;
      obj[id] = sectionData;
    });
    return obj;
  }, [sections]);

  const contextValue = {
    sections,
    addSection,
    updateSection,
    removeSection,
    setAllSections,
    getSectionsObject,
  };

  return (
    <EmailSectionContext.Provider value={contextValue}>
      {children}
    </EmailSectionContext.Provider>
  );
};

// Safe hook that returns defaults when used outside of a provider
export const useEmailSections = () => {
  const context = useContext(EmailSectionContext);
  return context || defaultContextValue;
};

// Higher-order component to wrap components that need section data
export const withEmailSections = (Component) => {
  return (props) => (
    <EmailSectionProvider initialSections={props.emailData || {}}>
      <Component {...props} />
    </EmailSectionProvider>
  );
};
